import React, { useState, useEffect } from 'react';
import api from '../services/api';

const GOLD = '#B8892A';
const INK = '#18140C';
const INK3 = '#7A7268';
const MUTED = '#685840';
const BG = '#FAF8F5';
const BD = '#E8E0D0';
const TEAL = '#1A6B54';

const FALLBACK_RATE = 0.043;

function Row({ label, value, strong, accent }) {
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      padding: '11px 0', borderBottom: `1px solid ${BD}40`,
    }}>
      <span style={{ fontSize: strong ? 15 : 13, color: strong ? INK : MUTED, fontWeight: strong ? 600 : 400 }}>{label}</span>
      <span style={{
        fontFamily: 'var(--mono,monospace)', fontSize: strong ? 18 : 14,
        fontWeight: strong ? 700 : 500, color: accent || (strong ? INK : INK3),
      }}>{value}</span>
    </div>
  );
}

export default function FeeCalculator() {
  const [hammer, setHammer] = useState('');
  const [taxRate, setTaxRate] = useState(10);       // 消费税 %
  const [houseRate, setHouseRate] = useState(10);   // 拍卖行手续费 %
  const [svcRate, setSvcRate] = useState(3);        // 代拍服务费 %
  const [rate, setRate] = useState(FALLBACK_RATE);  // JPY→CNY

  useEffect(() => {
    api.get('/exchange-rates/current').then(({ data }) => {
      const r = Number(data?.rate); if (r > 0) setRate(r);
    }).catch(() => {});
    api.get('/customers/me').then(({ data }) => {
      const s = Number(data?.service_fee_rate);
      if (s > 0) setSvcRate(+(s * 100).toFixed(2));
    }).catch(() => {});
  }, []);

  const H = Math.max(0, Number(hammer) || 0);
  const tax = Math.round(H * taxRate / 100);
  const houseFee = Math.round(H * houseRate / 100);
  const svcFee = Math.round(H * svcRate / 100);
  const subtotalJpy = H + tax + houseFee + svcFee;
  const totalCny = Math.round(subtotalJpy * rate);

  const inputStyle = {
    width: '100%', padding: '11px 12px', border: `1px solid ${BD}`, borderRadius: 8,
    fontSize: 15, color: INK, outline: 'none', boxSizing: 'border-box', background: '#fff',
  };
  const miniInput = {
    width: 64, padding: '6px 8px', border: `1px solid ${BD}`, borderRadius: 6,
    fontSize: 13, textAlign: 'right', outline: 'none', color: INK,
  };

  return (
    <div style={{ background: BG, minHeight: '100vh', padding: '20px 16px 40px', fontFamily: 'var(--sans,sans-serif)' }}>
      <div style={{ maxWidth: 560, margin: '0 auto' }}>
        <h2 style={{ fontSize: 20, fontWeight: 700, color: INK, marginBottom: 4 }}>费用计算器</h2>
        <p style={{ fontSize: 12, color: INK3, marginBottom: 18 }}>输入落槌价，估算到手总费用（不含国际/国内运费与保险）</p>

        {/* 落槌价输入 */}
        <div style={{ background: '#fff', border: `1px solid ${BD}`, borderRadius: 12, padding: 16, marginBottom: 14 }}>
          <label style={{ fontSize: 13, color: MUTED, display: 'block', marginBottom: 8 }}>落槌价（日元 JPY）</label>
          <input
            type="number" inputMode="numeric" value={hammer} placeholder="例如 50000"
            onChange={e => setHammer(e.target.value)} style={inputStyle}
          />

          <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
            {[
              { label: '消费税率', val: taxRate, set: setTaxRate },
              { label: '拍卖行手续费率', val: houseRate, set: setHouseRate },
              { label: '代拍服务费率', val: svcRate, set: setSvcRate },
            ].map(f => (
              <div key={f.label} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontSize: 13, color: MUTED }}>{f.label}</span>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <input type="number" value={f.val} onChange={e => f.set(Number(e.target.value) || 0)} style={miniInput} />
                  <span style={{ fontSize: 13, color: INK3 }}>%</span>
                </span>
              </div>
            ))}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontSize: 13, color: MUTED }}>汇率（中行卖出价）</span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <input type="number" step="0.0001" value={rate} onChange={e => setRate(Number(e.target.value) || 0)} style={{ ...miniInput, width: 80 }} />
                <span style={{ fontSize: 12, color: INK3 }}>CNY/JPY</span>
              </span>
            </div>
          </div>
        </div>

        {/* 明细 */}
        <div style={{ background: '#fff', border: `1px solid ${BD}`, borderRadius: 12, padding: '4px 16px 16px' }}>
          <Row label="落槌价" value={`¥${H.toLocaleString()}`} />
          <Row label={`消费税 (${taxRate}%)`} value={`¥${tax.toLocaleString()}`} />
          <Row label={`拍卖行手续费 (${houseRate}%)`} value={`¥${houseFee.toLocaleString()}`} />
          <Row label={`代拍服务费 (${svcRate}%)`} value={`¥${svcFee.toLocaleString()}`} />
          <Row label="日元小计" value={`¥${subtotalJpy.toLocaleString()}`} strong />
          <div style={{ height: 8 }} />
          <Row label="人民币合计" value={`≈ ¥${totalCny.toLocaleString()}`} strong accent={TEAL} />
        </div>

        <p style={{ fontSize: 11, color: INK3, marginTop: 14, lineHeight: 1.7 }}>
          * 以上为估算，实际以拍卖行规则与结算时汇率为准；<b>国际/国内运费、保险、关税</b>在中拍后按实际重量与渠道另计。
          费率可按具体拍卖行调整，代拍服务费率默认取您的会员等级费率。
        </p>
      </div>
    </div>
  );
}
